import { useEffect, useState } from 'react';

/**
 * ツールチップの表示状態（アンカー要素・内容）を保持し、表示・非表示のハンドラーを提供する
 *
 * @returns {Object} ツールチップの状態と表示・非表示のハンドラー
 * @returns {HTMLElement | null} anchorEl 表示中のトリガー要素
 * @returns {React.ReactNode} content 表示する内容
 * @returns {Function} showTooltip ツールチップを表示するハンドラー
 * @returns {Function} hideTooltip ツールチップを非表示にするハンドラー
 */
export const useTooltipHandler = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [content, setContent] = useState<React.ReactNode>(null);

  const showTooltip = (anchor: HTMLElement, tooltipContent: React.ReactNode) => {
    setAnchorEl(anchor);
    setContent(tooltipContent);
  };

  const hideTooltip = () => {
    setAnchorEl(null);
    setContent(null);
  };

  /**
   * Escape キー押下・スクロールでツールチップを閉じる。
   * EventListener の購読とクリーンアップが必要なため useEffect を使用する。
   */
  useEffect(() => {
    if (!anchorEl) return;
    // hideTooltip を呼ばず setState を直書きするのは、この effect の依存を [anchorEl] のみに保つため。
    // 毎レンダー再生成される hideTooltip を依存に含めると、リスナーを毎回張り替えることになる
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setAnchorEl(null);
      setContent(null);
    };
    const handleScroll = () => {
      setAnchorEl(null);
      setContent(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [anchorEl]);

  return { anchorEl, content, showTooltip, hideTooltip };
};
